'use client';
import { HostMode, HostType, RpcType } from '@repo/types/host';
import { useMemo } from 'react';
import { getHost } from '../host';

interface HostInfo {
    hostType: HostType;
    hostMode: HostMode;
    rpcType: RpcType;
}

let useSearchParams: () => URLSearchParams | null = () =>
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
try {
    const nextNavigation = require('next/navigation');
    useSearchParams = nextNavigation.useSearchParams;
} catch {
    // Next.js not available, will fall back to window.location
}

/**
 * Hook to get host information from URL parameters or Host instance
 * This replaces reading from Redux store
 */
export function useHostInfo(): HostInfo {
    const searchParams = useSearchParams();

    return useMemo(() => {
        try {
            const host = getHost();
            return {
                hostType: host.info.type,
                hostMode: host.info.mode,
                rpcType: host.info.rpc,
            };
        } catch {
            const hostType = (searchParams?.get('hostType') as HostType) ?? HostType.WEB;
            let hostMode = (searchParams?.get('hostMode') as HostMode) ?? HostMode.WINDOW;

            // Auto-detect embed mode if we're in an iframe
            if (typeof window !== 'undefined' && hostType === HostType.WEB && window.self !== window.top) {
                hostMode = HostMode.EMBED;
            }

            return {
                hostType,
                hostMode,
                rpcType: (searchParams?.get('rpcType') as RpcType) ?? RpcType.OPENER,
            };
        }
    }, [searchParams]);
}

/**
 * Hook to get just the host type
 */
export function useHostType(): HostType {
    return useHostInfo().hostType;
}

/**
 * Hook to get just the host mode
 */
export function useHostMode(): HostMode {
    return useHostInfo().hostMode;
}

/**
 * Hook to get just the RPC type
 */
export function useRpcType(): RpcType {
    return useHostInfo().rpcType;
}
